import { Add, KeyboardArrowDown, KeyboardArrowRight, Merge, TableChart, TableRows } from '@mui/icons-material';
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getRoles from '@src/APIs/graphQl/Role/getRoles';
import RoleCard from '@src/App/Components/Cards/EntityCards/Roles/RoleCard/RoleCard';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import MergeRolesDialog from '@src/App/Components/Dialogs/MergeRolesDialog/MergeRolesDialog';
import { RoleDialog } from '@src/App/Components/Dialogs/RoleDialog/RoleDialog';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import RoleOverlay from '@src/App/Components/Overlays/RoleOverlay/RoleOverlay';
import type { IDataTableField } from '@src/App/Components/Tables/DataTable/DataTable';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type Role from '@src/Objects/Role';
import type { AppState } from '@store/store';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import EntitiesPage, { EViewType, type IEntitiesPageAction } from '../../Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '../../Generic/EntitiesPage/ViewTypeMenu';

/**
 * Component representing the RolesPage.
 *
 * @returns {JSX.Element} The JSX element for the RolesPage.
 */
function RolesPage() {
    const navigate = useNavigate();
    const { openDialog } = useDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).roles;
    const emp = useSelector((state: AppState) => state.employee);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const maxLevel = levelDefinitions.length;
    const permissions = useSelector((state: AppState) => state.permissions);
    const entities = useSelector((state: AppState) => state.roles);

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    useEffect(() => {
        getRoles().catch((ex) => {
            setLoadingError(ex);
        });
    }, []);

    const [selected, setSelected] = useState<number[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const toggleRowExpansion = (roleId: number) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(roleId)) {
                newSet.delete(roleId);
            } else {
                newSet.add(roleId);
            }
            return newSet;
        });
    };

    const types: IViewType<Role>[] = [
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (_allEntities, innerEntries, _width, _selectedItems, tableSettings) => {
                if (!tableSettings) return <div>Loading...</div>;

                const hiddenFieldsSet = new Set(
                    Object.keys(tableSettings.hiddenFields).filter((key) => tableSettings.hiddenFields[key]),
                );
                const visibleFields = tableFields.filter((f) => !hiddenFieldsSet.has(f.id));
                const sortedFields = visibleFields.sort((a, b) => {
                    const orderA = tableSettings.order.indexOf(a.id);
                    const orderB = tableSettings.order.indexOf(b.id);
                    return orderA - orderB;
                });

                return (
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            {sortedFields.map((field) => (
                                                <TableCell
                                                    key={field.id}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((entity) => {
                                            const isExpanded = expandedRows.has(entity.id);
                                            const hasDescription =
                                                entity.description && entity.description.trim().length > 0;

                                            return (
                                                <Fragment key={entity.id}>
                                                    <TableRow
                                                        hover
                                                        onDoubleClick={() =>
                                                            navigate(entityTypeToLink(entity.id, EEntityType.ROLE))
                                                        }
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        {sortedFields.map((field) => (
                                                            <TableCell key={field.id}>
                                                                {field.id === 'id' && entity.id}
                                                                {field.id === 'title' && (
                                                                    <RoleOverlay roleId={entity.id}>
                                                                        {entity.title}
                                                                    </RoleOverlay>
                                                                )}
                                                                {field.id === 'description' && (
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        {hasDescription && (
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleRowExpansion(entity.id);
                                                                                }}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <KeyboardArrowDown />
                                                                                ) : (
                                                                                    <KeyboardArrowRight />
                                                                                )}
                                                                            </IconButton>
                                                                        )}
                                                                        <Box
                                                                            sx={{
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                                maxWidth: hasDescription
                                                                                    ? '200px'
                                                                                    : 'none',
                                                                            }}
                                                                        >
                                                                            {entity.description || 'Keine Beschreibung'}
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                {field.id === 'employees' && entity.employees.length}
                                                                {field.id === 'products' && entity.products.length}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                    {hasDescription && (
                                                        <TableRow>
                                                            <TableCell
                                                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                                                colSpan={sortedFields.length}
                                                            >
                                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                    <Box
                                                                        sx={{
                                                                            margin: 1,
                                                                            p: 2,
                                                                            bgcolor: 'background.default',
                                                                            borderRadius: 1,
                                                                        }}
                                                                    >
                                                                        <ExpandableText>
                                                                            <FormatedTextInput
                                                                                value={entity.description}
                                                                            />
                                                                        </ExpandableText>
                                                                    </Box>
                                                                </Collapse>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (_allEntities, innerEntries) => (
                <Container>
                    {innerEntries.map((entity) => (
                        <RoleCard key={entity.id} role={entity} />
                    ))}
                </Container>
            ),
        },
    ];

    const actions: IEntitiesPageAction[] = [
        {
            id: 'create',
            text: langStrings.newRole,
            icon: <Add />,
            filter() {
                if (!checkPermissions('Roles', 'Add', permissions)) return false;
                return true;
            },
            action() {
                return openDialog(<RoleDialog id="RoleDialog" />);
            },
        },
        {
            id: 'merge',
            text: langStrings.merge,
            icon: <Merge />,
            filter(selectedCount) {
                if (
                    !checkPermissions('Roles', 'Delete', permissions) ||
                    !checkPermissions('Roles', 'Edit', permissions)
                )
                    return false;
                return selectedCount > 1;
            },
            action(selectedIDs) {
                if (!this.filter(selectedIDs.length)) return;
                openDialog(
                    <MergeRolesDialog
                        id="MergeRolesDialog"
                        resolve={(result) => {
                            if (result) setSelected([]);
                        }}
                        roleIds={entities.filter((s) => selected.indexOf(s.id) > -1).map((c) => c.id)}
                    />,
                );
            },
        },
    ];

    const tableFields: IDataTableField<any>[] = [
        { id: 'id', label: 'ID', align: 'left', disablePadding: false, type: 'string' },
        { id: 'title', label: langStrings.roleName, align: 'left', disablePadding: false, type: 'string' },
        {
            id: 'description',
            label: langStrings.description,
            align: 'left',
            disablePadding: false,
            type: 'description',
        },
        { id: 'employees', label: langStrings.employees, align: 'center', disablePadding: false, type: 'string' },
        { id: 'products', label: langStrings.products, align: 'center', disablePadding: false, type: 'string' },
    ];

    if (!maxLevel) return <LoadingBox />;
    return (
        <EntitiesPage
            title={langStrings.roles}
            entities={entities}
            selected={selected}
            loadingError={loadingError}
            tableFields={tableFields}
            tableId="roles-table"
            filter={(entity, selectedView, searchText) =>
                selectedView.filter(entity) && textContainsLowerCase(searchText, entity.title)
            }
            setSelected={setSelected}
            views={[
                {
                    id: 'all',
                    title: langStrings.oftenUsed.allEntries,
                    filter() {
                        return true;
                    },
                },
                {
                    id: 'my',
                    title: langStrings.oftenUsed.myEntries,
                    filter(entity) {
                        return entity.employees.find((e) => e.id === emp?.id) !== undefined;
                    },
                },
            ]}
            types={types}
            actions={actions}
        />
    );
}

export default RolesPage;
